import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { Link } from "react-router-dom";
import { useState, useMemo } from "react";
import { API_BASE_URL } from "@/pages/blog/api";
import { useSEO } from "@/hooks/useSEO";
import { CONTACT_PAGE_SEO } from "@/constants/seoConfig";

export default function ContactPage() {
  const contactSeo = useMemo(() => CONTACT_PAGE_SEO, []);
  useSEO(contactSeo);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("submitting");

    const body = new URLSearchParams({
      name: formData.name,
      email: formData.email,
      subject: formData.subject,
      message: formData.message,
      pageUrl: typeof window !== 'undefined' ? window.location.href : '',
    });

    try {
      const res = await fetch(`${API_BASE_URL}/user/contactUs`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const data = (await res.json()) as { isSuccess?: boolean; message?: string };
      if (res.ok && data.isSuccess) {
        setStatus("success");
        setFormData({ name: "", email: "", subject: "", message: "" });
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />

      <main>
        <section
          className="pt-28 pb-10 md:pt-32 md:pb-16 px-4 md:px-8 relative overflow-hidden"
          style={{ background: "linear-gradient(160deg, #f5f0ff 0%, #ede8ff 60%, #faf8ff 100%)" }}
        >
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute w-96 h-96 rounded-full opacity-20 -top-20 -right-20" style={{ background: "radial-gradient(circle, #A855F7 0%, transparent 70%)" }} />
            <div className="absolute w-64 h-64 rounded-full opacity-15 bottom-0 left-10" style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 70%)" }} />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <nav aria-label="Breadcrumb" className="flex justify-center mb-4">
              <ol className="flex items-center gap-2 text-sm text-[#7a6aaa]">
                <li><Link to="/" className="hover:text-[#7C3AED] transition-colors">Home</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-[#7C3AED] font-medium">Contact</li>
              </ol>
            </nav>

            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#7C3AED]/20 bg-white text-[#7C3AED] text-sm font-medium mb-4">
              <i className="ri-customer-service-line"></i>
              We are here to help
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-[#1a1040] mb-3 leading-tight">
              Contact Support
            </h1>
            <p className="text-[#5a4a8a] text-base max-w-xl mx-auto">
              Have a question, feedback, or need help with Ava? Reach out and we will get back to you as soon as possible.
            </p>
          </div>
        </section>

        {/* Blend: gradient to white */}
        <div className="h-12 -mt-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #ffffff)" }} />

        <div className="bg-white">
          <div className="max-w-6xl mx-auto px-4 md:px-8 pt-12 md:pt-16 pb-20 md:pb-24">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div>
                  <h2 className="font-display text-lg font-bold text-[#1a1040] mb-4">Get in Touch</h2>
                  <p className="text-[#5a4a8a] text-sm leading-relaxed mb-6">
                    Whether you need help with your account, have a feature request, or just want to say hello, our team is ready to listen.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-white border border-[#e8e0ff]">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#f5f0ff] flex-shrink-0">
                      <i className="ri-mail-line text-[#7C3AED]"></i>
                    </div>
                    <div>
                      <p className="text-xs text-[#9a8aaa] mb-0.5">Email</p>
                      <span className="text-sm font-medium text-[#1a1040]">info@neuroava.com</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3">
                <div className="bg-white rounded-2xl border border-[#e8e0ff] p-6 md:p-8">
                  <h2 className="font-display text-lg font-bold text-[#1a1040] mb-1">Send a Message</h2>
                  <p className="text-[#7a6aaa] text-sm mb-6">Fill out the form below and we will respond within 24 hours.</p>

                  {status === "success" && (
                    <div className="mb-6 p-4 rounded-xl bg-[#f0fdf4] border border-[#bbf7d0] text-[#166534] text-sm flex items-center gap-2">
                      <i className="ri-checkbox-circle-line"></i>
                      Message sent successfully. We will be in touch soon!
                    </div>
                  )}
                  {status === "error" && (
                    <div className="mb-6 p-4 rounded-xl bg-[#fff1f2] border border-[#fecdd3] text-[#be123c] text-sm flex items-center gap-2">
                      <i className="ri-alert-line"></i>
                      Something went wrong. Please try again later.
                    </div>
                  )}

                  <form id="contact-form" onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-[#1a1040] mb-1.5">Name</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 rounded-lg border border-[#e0d8ff] bg-white text-sm text-[#1a1040] placeholder-[#aaa] outline-none focus:border-[#7C3AED] transition-colors"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-[#1a1040] mb-1.5">Email</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 rounded-lg border border-[#e0d8ff] bg-white text-sm text-[#1a1040] placeholder-[#aaa] outline-none focus:border-[#7C3AED] transition-colors"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-[#1a1040] mb-1.5">Subject</label>
                      <div className="relative">
                        <select
                          id="subject"
                          name="subject"
                          required
                          value={formData.subject}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 pr-10 rounded-lg border border-[#e0d8ff] bg-white text-sm text-[#1a1040] outline-none focus:border-[#7C3AED] transition-colors appearance-none"
                        >
                          <option value="" disabled>Choose a topic</option>
                          <option value="Account Help">Account Help</option>
                          <option value="Billing & Subscriptions">Billing &amp; Subscriptions</option>
                          <option value="Technical Issue">Technical Issue</option>
                          <option value="Feature Request">Feature Request</option>
                          <option value="Bug Report">Bug Report</option>
                          <option value="General Feedback">General Feedback</option>
                          <option value="Partnership">Partnership</option>
                          <option value="Other">Other</option>
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-[#7a6aaa] pointer-events-none text-base"></i>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-[#1a1040] mb-1.5">Message</label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 rounded-lg border border-[#e0d8ff] bg-white text-sm text-[#1a1040] placeholder-[#aaa] outline-none focus:border-[#7C3AED] transition-colors resize-none"
                        placeholder="Tell us how we can help..."
                      />
                      <p className="text-[#aaa] text-xs mt-1 text-right">{formData.message.length}/500</p>
                    </div>

                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="whitespace-nowrap w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 rounded-full text-white font-semibold text-sm cursor-pointer hover:opacity-90 transition-all disabled:opacity-60 border-none"
                      style={{ background: "linear-gradient(135deg, #7C3AED, #A855F7)" }}
                    >
                      {status === "submitting" ? (
                        <>
                          <i className="ri-loader-2-line animate-spin"></i>
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-fill"></i>
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Blend: white to gradient */}
      <div className="h-12 -mt-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #f5f0ff)" }} />
      <Footer />
    </div>
  );
}